import React, { useEffect, useMemo, useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { useDataVersion } from '../context/DataVersionContext';
import api from '../lib/api';
import { formatBRL, todayBR } from '../lib/format';
import { calcularPote, temRegra } from '../lib/distribuicao';
import {
  Wallet, PiggyBank, ArrowUpCircle, ArrowDownCircle, Calendar, ChevronDown,
  CreditCard, TrendingUp, TrendingDown, Minus, AlertTriangle, CalendarClock,
  Tags, Target, GripVertical, RotateCcw, ChevronRight,
} from 'lucide-react';
import { OverviewPanel, MonthlyIncomeExpenseChart } from '../components/DashboardCharts';
import DateInput from '../components/DateInput';
import { tintColor } from '../lib/colorTint';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Compute [start, end] ISO dates for a given preset.
function periodRange(preset) {
  const today = todayBR();
  const [Y, M, D] = today.split('-').map(Number);
  const lastDayOfMonth = new Date(Y, M, 0).getDate();
  const monthStart = `${Y}-${String(M).padStart(2, '0')}-01`;
  const monthEnd = `${Y}-${String(M).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;
  const subtractDays = (n) => {
    const d = new Date(Y, M - 1, D);
    d.setDate(d.getDate() - n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  switch (preset) {
    case 'today': return { start: today, end: today };
    case 'week': return { start: subtractDays(6), end: today };
    case 'last30': return { start: subtractDays(29), end: today };
    case 'last90': return { start: subtractDays(89), end: today };
    case 'year': return { start: `${Y}-01-01`, end: `${Y}-12-31` };
    case 'month':
    default: return { start: monthStart, end: monthEnd };
  }
}

// CAIXA vem primeiro e é o padrão: é a leitura mais concreta — "quanto saiu
// da conta" — e a que casa direto com o extrato bancário. Competência exige
// entender mês de referência e o ciclo do cartão; quem quiser, escolhe.
const BASES = [
  {
    id: 'caixa',
    label: 'Caixa',
    pergunta: 'Quando o dinheiro moveu',
    campo: 'expense_cash',
    outro: 'expense',
    rotuloOutro: 'Comprometido',
    detalhe: 'Sempre o mês da data. A compra no cartão só conta quando a fatura é paga.',
  },
  {
    id: 'competencia',
    label: 'Competência',
    pergunta: 'Quando eu me comprometi',
    campo: 'expense',
    outro: 'expense_cash',
    rotuloOutro: 'Saiu do caixa',
    detalhe: 'Mês de referência quando preenchido. A compra no cartão conta na data da compra.',
  },
];

const PRESETS = [
  { id: 'today', label: 'Hoje' },
  { id: 'week', label: 'Últimos 7 dias' },
  { id: 'month', label: 'Este mês' },
  { id: 'last30', label: 'Últimos 30 dias' },
  { id: 'last90', label: 'Últimos 90 dias' },
  { id: 'year', label: 'Este ano' },
  { id: 'custom', label: 'Personalizado' },
];

/*
 * As seções do painel, na ordem padrão. Cada uma pode ser arrastada para onde
 * o usuário preferir — o que importa para um varejista não é o mesmo que
 * importa para uma família, e nem sempre é o que estava no topo.
 *
 * A ordem escolhida fica no navegador: é preferência de visualização, não dado.
 * Ao acrescentar uma seção nova aqui, ela aparece no fim para quem já tem uma
 * ordem salva — ver `mesclarOrdem`.
 */
const SECOES = [
  { id: 'indicadores', titulo: 'Indicadores' },
  { id: 'vermelho', titulo: 'Potes no vermelho' },
  { id: 'faturas', titulo: 'Faturas em aberto' },
  { id: 'compromissos', titulo: 'Próximos compromissos' },
  { id: 'grafico', titulo: 'Receitas vs Despesas' },
  { id: 'plano', titulo: 'Planejado x realizado' },
  { id: 'tags', titulo: 'Gastos por tag' },
  { id: 'potes', titulo: 'Visão geral dos potes' },
  { id: 'contas', titulo: 'Visão geral das contas' },
  { id: 'movPotes', titulo: 'Movimentações por pote' },
  { id: 'movContas', titulo: 'Movimentações por conta' },
];
const ORDEM_PADRAO = SECOES.map((s) => s.id);
const CHAVE_ORDEM = 'dpots_dashboard_ordem';

/** Respeita a ordem salva, mas nunca esconde seção que ela não conhecia. */
function mesclarOrdem(salva) {
  const conhecidas = salva.filter((id) => ORDEM_PADRAO.includes(id));
  const novas = ORDEM_PADRAO.filter((id) => !conhecidas.includes(id));
  return [...conhecidas, ...novas];
}

export default function Dashboard() {
  const { currentProject } = useProject();
  const { version } = useDataVersion();
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [regras, setRegras] = useState([]);
  const [preset, setPreset] = useState('month');
  const [customStart, setCustomStart] = useState(() => periodRange('month').start);
  const [customEnd, setCustomEnd] = useState(() => periodRange('month').end);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [baseId, setBaseId] = useState(() => localStorage.getItem('dpots_base') || 'caixa');
  const base = BASES.find((b) => b.id === baseId) || BASES[0];

  const [ordem, setOrdem] = useState(() => {
    try {
      const salva = JSON.parse(localStorage.getItem(CHAVE_ORDEM) || 'null');
      return Array.isArray(salva) ? mesclarOrdem(salva) : ORDEM_PADRAO;
    } catch { return ORDEM_PADRAO; }
  });

  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const aoSoltar = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setOrdem((atual) => {
      const nova = arrayMove(atual, atual.indexOf(active.id), atual.indexOf(over.id));
      localStorage.setItem(CHAVE_ORDEM, JSON.stringify(nova));
      return nova;
    });
  };
  const restaurarOrdem = () => {
    setOrdem(ORDEM_PADRAO);
    localStorage.removeItem(CHAVE_ORDEM);
  };

  const range = useMemo(() => preset === 'custom'
    ? { start: customStart, end: customEnd }
    : periodRange(preset), [preset, customStart, customEnd]);

  useEffect(() => {
    if (!currentProject) return;
    setLoading(true);
    Promise.all([
      api.get(`/projects/${currentProject.id}/dashboard`, { params: { start: range.start, end: range.end, base: baseId } }),
      api.get(`/projects/${currentProject.id}/dashboard/monthly`, { params: { months: 6, base: baseId } }),
      api.get(`/projects/${currentProject.id}/monthly_distributions`),
    ]).then(([r, m, d]) => { setData(r.data); setMonthly(m.data); setRegras(d.data || []); })
      .finally(() => setLoading(false));
  }, [currentProject, range.start, range.end, version, baseId]);

  if (!currentProject) return <div className="text-slate-500 dark:text-slate-400">Selecione um projeto.</div>;
  if (loading || !data) return <div className="text-slate-500 dark:text-slate-400">Carregando...</div>;

  const currentLabel = PRESETS.find((p) => p.id === preset)?.label || 'Este mês';

  const despesa = data[base.campo] ?? 0;
  const despesaOutra = data[base.outro];
  const mostrarOutra = despesaOutra != null && Math.abs(despesaOutra - despesa) > 0.005;
  const anterior = data.previous;
  const despesaAnterior = anterior ? anterior[base.campo] : null;

  const trocarBase = (id) => { setBaseId(id); localStorage.setItem('dpots_base', id); };

  const blocos = {
    indicadores: (
      <Indicadores data={data} currentLabel={currentLabel} despesa={despesa}
        despesaOutra={despesaOutra} mostrarOutra={mostrarOutra} base={base}
        anterior={anterior} despesaAnterior={despesaAnterior} />
    ),
    vermelho: <PotesNoVermelho pots={data.pots} />,
    faturas: <FaturasEmAberto items={data.open_invoices} />,
    compromissos: <ProximosCompromissos items={data.upcoming} />,
    grafico: (
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Receitas vs Despesas (últimos 6 meses)</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Por {base.label.toLowerCase()}</p>
        <MonthlyIncomeExpenseChart data={monthly} expenseKey={base.campo} />
      </div>
    ),
    plano: <PlanejadoRealizado pots={data.pots} regras={regras} movimentos={data.pot_movements} receita={data.income} />,
    tags: <GastosPorTag items={data.tag_expenses} total={despesa} />,
    potes: (
      <OverviewPanel title="Visão Geral dos Potes" items={data.pots}
        totalLabel="Saldo Total em Potes" filterLabel="Filtrar por Tipo de Pote" />
    ),
    contas: (
      <OverviewPanel title="Visão Geral das Contas" items={data.accounts}
        totalLabel="Saldo Total em Contas" filterLabel="Filtrar por Tipo de Conta" />
    ),
    movPotes: <Movimentacoes title="Resumo de Movimentações por Pote" items={data.pot_movements || []} testId="pot-movements" />,
    movContas: <Movimentacoes title="Resumo de Movimentações por Conta" items={data.account_movements || []} testId="account-movements" />,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">Dashboard</h2>
          <p className="text-slate-500 dark:text-slate-400">{currentProject.name}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-0.5 shadow-sm" data-testid="dashboard-base">
            {BASES.map((b) => (
              <button key={b.id} onClick={() => trocarBase(b.id)}
                data-testid={`dashboard-base-${b.id}`}
                title={`${b.pergunta}. ${b.detalhe}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  baseId === b.id
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}>
                {b.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <button onClick={() => setShowPicker((v) => !v)} data-testid="dashboard-period-toggle"
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm">
              <Calendar className="w-4 h-4 text-indigo-600" />
              <span className="font-medium text-sm">{currentLabel}</span>
              <ChevronDown className="w-4 h-4 opacity-60" />
            </button>
            {showPicker && (
              <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-30 p-2 space-y-1" data-testid="dashboard-period-menu">
                {PRESETS.map((p) => (
                  <button key={p.id} onClick={() => { setPreset(p.id); if (p.id !== 'custom') setShowPicker(false); }}
                    data-testid={`dashboard-period-${p.id}`}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm ${preset===p.id?'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium':'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                    {p.label}
                  </button>
                ))}
                {preset === 'custom' && (
                  <div className="pt-2 border-t border-slate-100 dark:border-slate-700 mt-2 space-y-2">
                    <div>
                      <label className="text-xs text-slate-500 dark:text-slate-400">De</label>
                      <DateInput value={customStart} onChange={(v) => setCustomStart(v)} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 dark:text-slate-400">Até</label>
                      <DateInput value={customEnd} onChange={(v) => setCustomEnd(v)} />
                    </div>
                    <button onClick={() => setShowPicker(false)}
                      className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg">Aplicar</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 -mt-3">
        <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="dashboard-base-detalhe">
          <span className="font-medium text-slate-600 dark:text-slate-300">{base.pergunta}.</span> {base.detalhe}
        </div>
        <button onClick={restaurarOrdem} data-testid="dashboard-restaurar-ordem"
          title="Voltar as seções para a ordem original"
          className="shrink-0 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-600">
          <RotateCcw className="w-3 h-3" /> ordem padrão
        </button>
      </div>

      <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={aoSoltar}>
        <SortableContext items={ordem} strategy={verticalListSortingStrategy}>
          <div className="space-y-6">
            {ordem.map((id) => (
              <SecaoArrastavel key={id} id={id} titulo={SECOES.find((s) => s.id === id)?.titulo}>
                {blocos[id]}
              </SecaoArrastavel>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

/* ------------------------------------------------------------------ seções */

/** Envelope arrastável. A alça aparece ao passar o mouse, para não poluir. */
function SecaoArrastavel({ id, titulo, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  if (!children) return null;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`secao-${id}`}>
      <button {...attributes} {...listeners} type="button"
        title={`Arraste para reposicionar: ${titulo || ''}`}
        className="absolute -left-7 top-3 hidden md:flex items-center justify-center w-6 h-6 rounded-md
                   text-slate-300 dark:text-slate-600 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30
                   opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
        <GripVertical className="w-4 h-4" />
      </button>
      {children}
    </div>
  );
}

function Indicadores({ data, currentLabel, despesa, despesaOutra, mostrarOutra, base, anterior, despesaAnterior }) {
  // Potes e contas classificam o MESMO dinheiro — por finalidade e por lugar.
  // Em um cadastro íntegro os dois somam igual. Quando divergem, existe
  // lançamento com pote e sem conta (ou o contrário): é sinal de cadastro
  // incompleto, não de erro de cálculo.
  const diferenca = (data.total_pots || 0) - (data.total_accounts || 0);
  const divergente = Math.abs(diferenca) > 0.005;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Wallet} label="Saldo em Potes" value={data.total_pots} color="indigo" />
        <StatCard icon={PiggyBank} label="Saldo em Contas" value={data.total_accounts} color="blue" />
        <StatCard icon={ArrowUpCircle} label={`Receitas (${currentLabel})`} value={data.income} color="emerald"
          variacao={<Variacao atual={data.income} anterior={anterior?.income} periodo={anterior?.periodo} />} />
        <StatCard icon={ArrowDownCircle} label={`Despesas (${currentLabel})`} value={despesa} color="rose"
          note={mostrarOutra ? `${base.rotuloOutro}: ${formatBRL(despesaOutra)}` : null}
          variacao={<Variacao atual={despesa} anterior={despesaAnterior} periodo={anterior?.periodo} menorEhMelhor />} />
      </div>
      {divergente && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
          data-testid="divergencia-potes-contas">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="leading-snug">
            Potes e contas divergem em <strong>{formatBRL(Math.abs(diferenca))}</strong>. É o mesmo dinheiro
            classificado de dois jeitos, então deveriam somar igual — a diferença indica lançamento com
            pote e sem conta, ou o contrário. Vale conferir os lançamentos recentes.
          </div>
        </div>
      )}
    </div>
  );
}

/** Um número sozinho não diz se está bom. Este é o contexto que faltava. */
function Variacao({ atual, anterior, periodo, menorEhMelhor = false }) {
  if (anterior == null) return null;
  if (Math.abs(anterior) < 0.005) {
    return <span className="text-xs text-slate-400 dark:text-slate-500">sem base de comparação</span>;
  }
  const delta = (atual || 0) - anterior;
  const pct = (delta / Math.abs(anterior)) * 100;
  if (Math.abs(pct) < 0.5) {
    return <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
      <Minus className="w-3 h-3" /> estável vs {periodo || 'período anterior'}
    </span>;
  }
  const subiu = delta > 0;
  const favoravel = menorEhMelhor ? !subiu : subiu;
  const Icone = subiu ? TrendingUp : TrendingDown;
  const cor = favoravel ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400';
  return (
    <span className={`text-xs flex items-center gap-1 ${cor}`} title={`Período anterior: ${formatBRL(anterior)}`}>
      <Icone className="w-3 h-3" />
      {subiu ? '+' : ''}{pct.toFixed(0)}% vs {periodo || 'anterior'}
    </span>
  );
}

/* O gráfico de rosca dos potes mostra só saldos positivos — então os potes no
   vermelho, que são justamente os que pedem atenção, ficavam invisíveis. */
function PotesNoVermelho({ pots }) {
  const vermelhos = (pots || []).filter((p) => (p.balance || 0) < -0.005)
    .sort((a, b) => a.balance - b.balance);
  if (vermelhos.length === 0) return null;
  const total = vermelhos.reduce((s, p) => s + p.balance, 0);
  return (
    <div className="card p-5 border-rose-200 dark:border-rose-900/50" data-testid="potes-vermelho">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          Potes no vermelho
          <span className="text-xs font-normal text-slate-500 dark:text-slate-400">({vermelhos.length})</span>
        </h3>
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Total <span className="font-semibold text-rose-600 dark:text-rose-400">{formatBRL(total)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {vermelhos.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-rose-100 dark:border-rose-900/40 bg-rose-50/50 dark:bg-rose-900/10 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color || '#e11d48' }} />
              <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{p.name}</span>
            </div>
            <span className="text-sm font-semibold text-rose-600 dark:text-rose-400 tabular-nums whitespace-nowrap">
              {formatBRL(p.balance)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaturasEmAberto({ items }) {
  const abertas = (items || []).filter((f) => Math.abs(f.balance || 0) > 0.005);
  if (abertas.length === 0) return null;
  const total = abertas.reduce((s, f) => s + (f.balance || 0), 0);
  return (
    <div className="card p-5" data-testid="open-invoices">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-indigo-600" /> Faturas em aberto
        </h3>
        {abertas.length > 1 && (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Total <span className="font-semibold text-slate-800 dark:text-slate-100">{formatBRL(total)}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {abertas.map((f) => (
          <div key={f.id} className="rounded-xl border-2 p-4 bg-white dark:bg-slate-800"
            style={{ borderColor: f.color || '#6366f1', background: tintColor(f.color, 0.10) }}>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: f.color || '#6366f1' }} />
              <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{f.name}</div>
            </div>
            <div className="text-xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">{formatBRL(f.balance)}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">comprado e ainda não pago</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* O painel só olhava para trás. Isto olha para a frente. */
function ProximosCompromissos({ items }) {
  const lista = items || [];
  if (lista.length === 0) return null;
  const pagar = lista.filter((i) => i.type !== 'receivable').reduce((s, i) => s + (i.amount || 0), 0);
  const receber = lista.filter((i) => i.type === 'receivable').reduce((s, i) => s + (i.amount || 0), 0);
  const vencidos = lista.filter((i) => i.overdue).length;
  return (
    <div className="card p-5" data-testid="proximos-compromissos">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-indigo-600" /> Próximos 30 dias
          {vencidos > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
              {vencidos} vencido{vencidos > 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <div className="text-sm text-slate-500 dark:text-slate-400 flex gap-4">
          <span>A pagar <strong className="text-rose-600 dark:text-rose-400">{formatBRL(pagar)}</strong></span>
          {receber > 0 && <span>A receber <strong className="text-emerald-600 dark:text-emerald-400">{formatBRL(receber)}</strong></span>}
        </div>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-700">
        {lista.slice(0, 10).map((i) => (
          <div key={i.id} className="flex items-center justify-between gap-3 py-2">
            <div className="flex items-center gap-3 min-w-0">
              <span className={`shrink-0 px-2 py-0.5 rounded-md text-[11px] font-medium whitespace-nowrap ${
                i.overdue
                  ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                  : i.days_left <= 3
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
              }`}>
                {i.overdue ? 'vencido' : i.days_left === 0 ? 'hoje' : `em ${i.days_left}d`}
              </span>
              <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{i.description || '(sem descrição)'}</span>
            </div>
            <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ${
              i.type === 'receivable' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-800 dark:text-slate-100'
            }`}>
              {formatBRL(i.amount)}
            </span>
          </div>
        ))}
      </div>
      {lista.length > 10 && (
        <div className="pt-2 text-xs text-slate-500 dark:text-slate-400">
          e mais {lista.length - 10} — veja em A Pagar/Receber
        </div>
      )}
    </div>
  );
}

/* Você mantém regras de distribuição; o painel nunca as mencionava. Duas
   funcionalidades que existiam e não conversavam. */
function PlanejadoRealizado({ pots, regras, movimentos, receita }) {
  const linhas = (pots || [])
    .map((p) => {
      const regra = (regras || []).find((r) => r.pot_id === p.id);
      if (!temRegra(regra)) return null;
      const planejado = calcularPote(regra, receita || 0);
      const mov = (movimentos || []).find((m) => m.id === p.id);
      const realizado = mov ? (mov.income || 0) : 0;
      return { ...p, planejado, realizado, delta: realizado - planejado };
    })
    .filter(Boolean)
    .sort((a, b) => b.planejado - a.planejado);

  if (linhas.length === 0) return null;
  const totalPlan = linhas.reduce((s, l) => s + l.planejado, 0);
  const totalReal = linhas.reduce((s, l) => s + l.realizado, 0);

  return (
    <div className="card p-5" data-testid="planejado-realizado">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Target className="w-4 h-4 text-indigo-600" /> Planejado x realizado
        </h3>
        <div className="text-sm text-slate-500 dark:text-slate-400">
          {formatBRL(totalReal)} de {formatBRL(totalPlan)} planejados
        </div>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        O planejado usa suas regras de Distribuição sobre a receita do período ({formatBRL(receita || 0)}).
        O realizado é o que de fato entrou em cada pote.
      </p>
      <div className="space-y-2.5">
        {linhas.map((l) => {
          const proporcao = l.planejado > 0 ? Math.min((l.realizado / l.planejado) * 100, 150) : 0;
          const faltou = l.delta < -0.005;
          return (
            <div key={l.id}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm mb-1">
                <span className="text-slate-700 dark:text-slate-200 truncate max-w-full">{l.name}</span>
                <span className="text-slate-500 dark:text-slate-400 tabular-nums whitespace-nowrap text-xs sm:text-sm">
                  {formatBRL(l.realizado)} / {formatBRL(l.planejado)}
                  <span className={`ml-2 font-semibold ${faltou ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                    {l.planejado > 0 ? `${((l.realizado / l.planejado) * 100).toFixed(0)}%` : '—'}
                  </span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                <div className={`h-full rounded-full ${faltou ? 'bg-amber-400' : 'bg-emerald-500'}`}
                  style={{ width: `${proporcao}%`, background: proporcao >= 100 ? undefined : undefined }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Os potes dizem de qual orçamento saiu; as tags dizem com o quê. */
function GastosPorTag({ items, total }) {
  const lista = items || [];
  if (lista.length === 0) return null;
  const maior = Math.max(...lista.map((t) => t.total), 1);
  const somaTags = lista.reduce((s, t) => s + t.total, 0);
  return (
    <div className="card p-5" data-testid="gastos-por-tag">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Tags className="w-4 h-4 text-indigo-600" /> Gastos por tag
        </h3>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {formatBRL(somaTags)} classificados
          {total > 0.005 && ` · ${((somaTags / total) * 100).toFixed(0)}% das despesas`}
        </div>
      </div>
      {/* No celular o nome vai em cima da barra; lado a lado, sobrava pouco
          espaço para a barra e o valor ficava cortado. */}
      <div className="space-y-3 sm:space-y-2">
        {lista.map((t) => (
          <div key={t.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <div className="sm:w-44 sm:shrink-0 flex items-baseline justify-between gap-2 sm:block">
              <span className="text-sm text-slate-700 dark:text-slate-200 truncate" title={t.name}>{t.name}</span>
              <span className="sm:hidden text-sm font-semibold text-slate-800 dark:text-slate-100 tabular-nums whitespace-nowrap">
                {formatBRL(t.total)} <span className="text-xs font-normal text-slate-400">{t.count}x</span>
              </span>
            </div>
            <div className="flex-1 h-4 sm:h-5 rounded-md bg-slate-100 dark:bg-slate-700 overflow-hidden">
              <div className="h-full rounded-md bg-indigo-500/80" style={{ width: `${(t.total / maior) * 100}%` }} />
            </div>
            <div className="hidden sm:block w-28 shrink-0 text-right text-sm font-semibold text-slate-800 dark:text-slate-100 tabular-nums">
              {formatBRL(t.total)}
            </div>
            <div className="hidden sm:block w-10 shrink-0 text-right text-xs text-slate-400">{t.count}x</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Antes mostrava TODOS os potes, inclusive os zerados — uma parede de cartões
   iguais. Agora esconde os sem movimento e ordena pelos maiores. */
function Movimentacoes({ title, items, testId }) {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const comMovimento = (items || []).filter((i) => Math.abs(i.income || 0) > 0.005 || Math.abs(i.expense || 0) > 0.005);
  const ocultos = (items || []).length - comMovimento.length;
  const lista = (mostrarTodos ? items : comMovimento)
    .slice()
    .sort((a, b) => (Math.abs(b.income || 0) + Math.abs(b.expense || 0)) - (Math.abs(a.income || 0) + Math.abs(a.expense || 0)));

  if (!items || items.length === 0) return null;
  return (
    <div className="card p-6" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
        {ocultos > 0 && (
          <button onClick={() => setMostrarTodos((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600 dark:text-slate-400">
            <ChevronRight className={`w-3 h-3 transition-transform ${mostrarTodos ? 'rotate-90' : ''}`} />
            {mostrarTodos ? `ocultar os ${ocultos} sem movimento` : `mostrar os ${ocultos} sem movimento`}
          </button>
        )}
      </div>
      {lista.length === 0 ? (
        <div className="text-sm text-slate-400 dark:text-slate-500">Nenhuma movimentação no período.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lista.map((it) => {
            const bg = tintColor(it.color, 0.10);
            return (
              <div key={it.id} className="rounded-xl border-2 p-4 bg-white dark:bg-slate-800"
                style={{ borderColor: it.color || '#cbd5e1', background: bg }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: it.color || '#94a3b8' }} />
                  <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{it.name}</div>
                </div>
                <div className="space-y-1.5 text-sm">
                  <Row label="Receitas" value={it.income} positive />
                  <Row label="Despesas" value={it.expense} negative />
                  <div className="border-t border-slate-200 dark:border-slate-700 my-1.5" />
                  <Row label="Saldo" value={it.balance} bold signed />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, positive, negative, bold, signed }) {
  const color = positive ? 'text-emerald-600 dark:text-emerald-400'
    : negative ? 'text-rose-600 dark:text-rose-400'
    : (signed && value < 0) ? 'text-rose-600 dark:text-rose-400'
    : 'text-slate-800 dark:text-slate-100';
  return (
    <div className="flex items-center justify-between">
      <span className={`text-slate-600 dark:text-slate-400 ${bold ? 'font-semibold' : ''}`}>{label}</span>
      <span className={`${color} ${bold ? 'font-bold' : 'font-medium'} tabular-nums`}>
        {signed && value < 0 ? '-' : ''}{formatBRL(Math.abs(value || 0))}
      </span>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, note = null, variacao = null }) {
  const colors = {
    indigo: 'from-indigo-500 to-blue-500',
    blue: 'from-blue-500 to-cyan-500',
    emerald: 'from-emerald-500 to-teal-500',
    rose: 'from-rose-500 to-pink-500',
  };
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${colors[color]} shadow-md`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div className="text-sm text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${value < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-slate-100'}`}>{formatBRL(value)}</div>
      {note && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid="statcard-note">{note}</div>}
      {variacao && <div className="mt-1">{variacao}</div>}
    </div>
  );
}
